/**
 * Daily Challenge Page
 */

import { useEffect } from 'react';
import { useDailyStore } from '../stores/daily';
import { shareResult } from '../lib/share';
import { detectPlatform } from '../platform/detect';

// Loading Screen
function DailyLoading() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-900 to-indigo-900 overflow-hidden flex items-center justify-center">
      <div className="text-center">
        <div className="text-6xl mb-4 animate-bounce">📅</div>
        <p className="text-white text-xl">Загрузка Daily Challenge...</p>
      </div>
    </div>
  );
}

// Start Screen
function DailyStart() {
  const startGame = useDailyStore((s) => s.startGame);
  const questions = useDailyStore((s) => s.questions);
  const date = useDailyStore((s) => s.date);

  const formattedDate = date ? new Date(date).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
  }) : '';

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-900 to-indigo-900 overflow-hidden flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        <button
          onClick={() => window.location.href = '/'}
          className="px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-sm text-white transition-all mb-4"
        >
          ← Меню
        </button>
        <div className="text-center">
          <div className="text-7xl mb-4">📅</div>
          <h1 className="text-3xl font-bold text-white mb-2">Daily Challenge</h1>
          <p className="text-purple-200 mb-6">{formattedDate}</p>

          <div className="bg-white/10 rounded-2xl p-6 mb-6 text-left">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-2xl">📝</span>
              <span className="text-white">{questions.length} вопросов</span>
            </div>
            <div className="flex items-center gap-3 mb-3">
              <span className="text-2xl">⏱</span>
              <span className="text-white">20 секунд на вопрос</span>
            </div>
            <div className="flex items-center gap-3 mb-3">
              <span className="text-2xl">🎯</span>
              <span className="text-white">Очки за скорость</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-2xl">🏆</span>
              <span className="text-white">Соревнуйся за топ!</span>
            </div>
          </div>

          <div className="bg-yellow-500/20 rounded-xl p-4 mb-6">
            <p className="text-yellow-200 text-sm">
              ⚡ Награды: +50 XP за участие, до +200 XP за топ!
            </p>
          </div>

          <button
            onClick={startGame}
            className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 text-white font-bold py-4 px-8 rounded-2xl text-xl shadow-lg transition-transform"
          >
            🚀 Начать!
          </button>
        </div>
      </div>
    </div>
  );
}

// Already Played Screen
function DailyAlreadyPlayed() {
  const previousResult = useDailyStore((s) => s.previousResult);
  const date = useDailyStore((s) => s.date);

  const formattedDate = date ? new Date(date).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
  }) : '';

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-900 to-indigo-900 overflow-hidden flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        <button
          onClick={() => window.location.href = '/'}
          className="px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-sm text-white transition-all mb-4"
        >
          ← Меню
        </button>
        <div className="text-center">
          <div className="text-7xl mb-4">✅</div>
          <h1 className="text-3xl font-bold text-white mb-2">Уже сыграно!</h1>
          <p className="text-purple-200 mb-6">{formattedDate}</p>

          {previousResult && (
            <div className="bg-white/10 rounded-2xl p-6 mb-6">
              <div className="text-4xl font-bold text-yellow-400 mb-2">
                {previousResult.score.toLocaleString()}
              </div>
              <p className="text-purple-200 mb-4">очков</p>

              <div className="grid grid-cols-2 gap-4 text-left">
                <div>
                  <p className="text-purple-300 text-sm">Правильных</p>
                  <p className="text-white text-xl font-bold">
                    {previousResult.correctAnswers}/10
                  </p>
                </div>
                <div>
                  <p className="text-purple-300 text-sm">Рейтинг</p>
                  <p className="text-white text-xl font-bold">
                    Топ {previousResult.percentile}%
                  </p>
                </div>
              </div>

              {previousResult.grid && (
                <div className="mt-4 text-2xl tracking-wider">
                  {formatGrid(previousResult.grid)}
                </div>
              )}
            </div>
          )}

          <div className="bg-blue-500/20 rounded-xl p-4 mb-6">
            <p className="text-blue-200">
              ⏰ Новый Daily Challenge завтра в 00:00 МСК
            </p>
          </div>

          
          <a
            href="/swipe"
            className="block w-full bg-white/20 text-white font-bold py-3 px-6 rounded-xl hover:bg-white/30 transition"
          >
            🧠 Играть BrainSwipe
          </a>
        </div>
      </div>
    </div>
  );
}

// Playing Screen
function DailyPlaying() {
  const questions = useDailyStore((s) => s.questions);
  const currentIndex = useDailyStore((s) => s.currentIndex);
  const timeLeft = useDailyStore((s) => s.timeLeft);
  const selectedAnswerIndex = useDailyStore((s) => s.selectedAnswerIndex);
  const selectAnswer = useDailyStore((s) => s.selectAnswer);

  const question = questions[currentIndex];
  if (!question) return null;

  const progress = ((currentIndex) / questions.length) * 100;
  const timerProgress = (timeLeft / 20) * 100;
  const isUrgent = timeLeft <= 5;

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-900 to-indigo-900 overflow-hidden flex flex-col">
      {/* Header */}
      <div className="p-4">
        {/* Progress bar */}
        <div className="h-2 bg-white/20 rounded-full mb-3 overflow-hidden">
          <div
            className="h-full bg-yellow-400 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="flex justify-between items-center">
          <span className="text-white font-medium">
            {currentIndex + 1} / {questions.length}
          </span>

          {/* Timer */}
          <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${
            isUrgent ? 'bg-red-500 animate-pulse' : 'bg-white/20'
          }`}>
            <span className="text-white font-bold">{timeLeft}</span>
            <span className="text-white/70">сек</span>
          </div>
        </div>

        {/* Timer bar */}
        <div className="h-1 bg-white/10 rounded-full mt-2 overflow-hidden">
          <div
            className={`h-full transition-all duration-1000 ${
              isUrgent ? 'bg-red-500' : 'bg-green-400'
            }`}
            style={{ width: `${timerProgress}%` }}
          />
        </div>
      </div>

      {/* Question */}
      <div className="flex-1 flex flex-col p-4">
        <div className="bg-white/10 rounded-2xl p-6 mb-6">
          {question.quizTopic && (
            <div className="mb-2 pb-2 border-b border-white/10">
              <span className="text-xs text-purple-300">📚 Тема:</span>
              <span className="ml-2 text-sm font-medium text-purple-200">{question.quizTopic}</span>
            </div>
          )}
          {question.categoryEmoji && (
            <span className="text-2xl mb-2 block">{question.categoryEmoji}</span>
          )}
          <h2 className="text-xl text-white font-medium leading-relaxed">
            {question.text}
          </h2>
        </div>

        {/* Answers */}
        <div className="space-y-3">
          {question.options.map((answer, index) => (
            <button
              key={index}
              onClick={() => selectAnswer(index)}
              disabled={selectedAnswerIndex !== null}
              className={`w-full p-4 rounded-xl text-left font-medium transition-all ${
                selectedAnswerIndex === index
                  ? 'bg-yellow-500 text-white'
                  : 'bg-white/90 text-gray-800 hover:bg-white'
              } ${selectedAnswerIndex !== null && selectedAnswerIndex !== index ? 'opacity-50' : ''}`}
            >
              <span className="inline-block w-8 h-8 rounded-full bg-purple-100 text-purple-600 text-center leading-8 mr-3">
                {String.fromCharCode(65 + index)}
              </span>
              {answer}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// Feedback Screen (brief, auto-advances)
function DailyFeedback() {
  const isSubmitting = useDailyStore((s) => s.isSubmitting);

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-900 to-indigo-900 overflow-hidden flex items-center justify-center">
      <div className="text-center">
        <div className="text-6xl mb-4 animate-bounce">
          {isSubmitting ? '📤' : '⏳'}
        </div>
        <p className="text-white text-xl">
          {isSubmitting ? 'Отправка результатов...' : 'Следующий вопрос...'}
        </p>
      </div>
    </div>
  );
}

// Finished Screen
function formatGrid(grid: string | string[]): string {
  if (typeof grid === 'string') {
    return grid
      .replace(/fast/g, '🟩')
      .replace(/slow/g, '🟨')
      .replace(/wrong/g, '🟥');
  }
  return grid.map(g => g === 'fast' ? '🟩' : g === 'slow' ? '🟨' : '🟥').join('');
}

function DailyFinished() {
  const result = useDailyStore((s) => s.result);
  const error = useDailyStore((s) => s.error);
  const reset = useDailyStore((s) => s.reset);

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-900 to-indigo-900 overflow-hidden flex flex-col items-center justify-center p-4">
        <div className="text-center">
          <div className="text-6xl mb-4">😔</div>
          <h1 className="text-2xl font-bold text-white mb-4">{error}</h1>
          <button
            onClick={reset}
            className="bg-white/20 text-white py-3 px-6 rounded-xl"
          >
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  if (!result) return null;

  const getMedal = (percentile: number) => {
    if (percentile <= 1) return '🏆';
    if (percentile <= 10) return '🥇';
    if (percentile <= 25) return '🥈';
    if (percentile <= 50) return '🥉';
    return '🎖';
  };

  const getTitle = (percentile: number) => {
    if (percentile <= 1) return 'Чемпион дня!';
    if (percentile <= 10) return 'В элите!';
    if (percentile <= 25) return 'Отличный результат!';
    if (percentile <= 50) return 'Хорошо!';
    return 'Продолжай тренироваться!';
  };

  const shareText = `📅 Daily Challenge\n\n${formatGrid(result.grid)}\n\n🎯 ${result.score.toLocaleString()} очков\n✅ ${result.correctAnswers}/${result.totalQuestions}\n🏆 Топ ${result.percentile}%`;

  const handleShare = () => {
    const platform = detectPlatform();
    
    let url: string;
    if (platform === 'vk') {
      url = 'https://vk.com/app52236695#daily';
    } else if (platform === 'telegram') {
      url = 'https://t.me/qwizmug_bot?start=daily';
    } else {
      url = 'https://beshek.mugstel.ru/daily';
    }
    
    shareResult({ text: shareText, url });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-900 to-indigo-900 overflow-hidden flex flex-col items-center justify-center p-4">
      <div className="max-w-md w-full">
        <button
          onClick={() => window.location.href = '/'}
          className="px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-sm text-white transition-all mb-4"
        >
          ← Меню
        </button>
        <div className="text-center">
          <div className="text-7xl mb-2">{getMedal(result.percentile)}</div>
          <h1 className="text-3xl font-bold text-white mb-1">{getTitle(result.percentile)}</h1>
          <p className="text-purple-200 mb-6">Daily Challenge завершён</p>

          <div className="bg-white/10 rounded-2xl p-6 mb-4">
            <div className="text-5xl font-bold text-yellow-400 mb-1">
              {result.score.toLocaleString()}
            </div>
            <p className="text-purple-200">очков</p>
          </div>

          {result.grid && (
            <div className="text-3xl tracking-widest mb-4">
              {formatGrid(result.grid)}
            </div>
          )}

          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="bg-white/10 rounded-xl p-3">
              <p className="text-purple-300 text-xs">Правильных</p>
              <p className="text-white text-xl font-bold">
                {result.correctAnswers}/{result.totalQuestions}
              </p>
            </div>
            <div className="bg-white/10 rounded-xl p-3">
              <p className="text-purple-300 text-xs">Рейтинг</p>
              <p className="text-white text-xl font-bold">
                Топ {result.percentile}%
              </p>
            </div>
            <div className="bg-white/10 rounded-xl p-3">
              <p className="text-purple-300 text-xs">Место</p>
              <p className="text-white text-xl font-bold">
                #{result.position}
              </p>
            </div>
          </div>

          <div className="bg-yellow-500/20 rounded-xl p-3 mb-6">
            <p className="text-yellow-200">
              ⚡ +{result.xpAwarded} XP • Серия: {result.dailyStreak} {result.dailyStreak === 1 ? 'день' : 'дней'}
            </p>
          </div>

          <div className="space-y-3">
            <button
              onClick={handleShare}
              className="w-full bg-gradient-to-r from-green-400 to-emerald-500 text-white font-bold py-3 px-6 rounded-xl"
            >
              📤 Поделиться
            </button>
            
            <a
              href="/swipe"
              className="block w-full bg-white/20 text-white font-bold py-3 px-6 rounded-xl"
            >
              🧠 Играть BrainSwipe
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

// Main Page Component
export function DailyChallengePage() {
  const screen = useDailyStore((s) => s.screen);
  const loadDaily = useDailyStore((s) => s.loadDaily);
  const reset = useDailyStore((s) => s.reset);

  useEffect(() => {
    reset();
    loadDaily();
  }, [loadDaily, reset]);

  switch (screen) {
    case 'loading':
      return <DailyLoading />;
    case 'start':
      return <DailyStart />;
    case 'already_played':
      return <DailyAlreadyPlayed />;
    case 'playing':
      return <DailyPlaying />;
    case 'feedback':
      return <DailyFeedback />;
    case 'finished':
      return <DailyFinished />;
    default:
      return <DailyLoading />;
  }
}